import { useState, useRef, useEffect } from 'react';
import { clsx } from 'clsx';

interface SubmitResult {
  success: boolean;
  sakNr: string;
  message: string;
}

interface PublicCategory {
  id: string;
  navn: string;
  farge: string;
  beskrivelse?: string | null;
}

export default function PublicSubmitPage() {
  const [navn, setNavn] = useState('');
  const [epost, setEpost] = useState('');
  const [tittel, setTittel] = useState('');
  const [beskrivelse, setBeskrivelse] = useState('');
  const [kategori, setKategori] = useState('');
  const [categories, setCategories] = useState<PublicCategory[]>([]);
  const [categoriesLoading, setCategoriesLoading] = useState(true);
  const [file, setFile] = useState<File | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState<SubmitResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Hent aktive kategorier ved mount
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch('/api/public/categories', { credentials: 'include' });
        if (!res.ok) throw new Error('Kunne ikke hente kategorier');
        const data = (await res.json()) as PublicCategory[];
        // Backend kan returnere { $values: [...] } – unwrapp hvis nødvendig
        const list = (data as any).$values ?? data;
        if (!cancelled) setCategories(list);
      } catch {
        // Hvis henting feiler, lar vi kategori være valgfri (graceful degradation)
        if (!cancelled) setCategories([]);
      } finally {
        if (!cancelled) setCategoriesLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!navn.trim() || !epost.trim() || !tittel.trim() || !kategori) return;

    setSubmitting(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('navn', navn.trim());
      formData.append('epost', epost.trim());
      formData.append('tittel', tittel.trim());
      formData.append('kategori', kategori);
      formData.append('beskrivelse', beskrivelse);
      formData.append('website', '');
      if (file) formData.append('file', file);

      const res = await fetch('/api/public/submit', {
        method: 'POST',
        body: formData,
        credentials: 'include',
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.message || 'Noe gikk galt. Prøv igjen senere.');
      }

      setResult(data);
      setNavn('');
      setEpost('');
      setTittel('');
      setBeskrivelse('');
      setKategori('');
      setFile(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  };

  // Suksess-skjerm
  if (result) {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-slate-50 px-4 dark:bg-slate-950">
        <div className="pointer-events-none absolute inset-0 bg-gradient-mesh dark:bg-gradient-mesh-dark" />
        <div className="pointer-events-none absolute -right-40 -top-40 h-96 w-96 animate-pulse-soft rounded-full bg-green-500/10 blur-3xl" />

        <div className="relative w-full max-w-md animate-scale-in text-center">
          <div className="mx-auto mb-6 flex h-20 w-20 items-center justify-center rounded-3xl bg-gradient-to-br from-green-400 to-emerald-600 shadow-lg shadow-green-500/30">
            <svg className="h-10 w-10 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
              <path d="M20 6 9 17l-5-5" />
            </svg>
          </div>
          <h1 className="mb-2 text-2xl font-bold text-slate-800 dark:text-slate-100">Sak registrert!</h1>
          <p className="mb-1 text-sm text-slate-600 dark:text-slate-400">
            Din sak er registrert som
          </p>
          <p className="mb-6 font-mono text-2xl font-bold text-gradient">{result.sakNr}</p>
          <div className="glass mb-6 rounded-2xl p-4">
            <p className="text-sm text-slate-600 dark:text-slate-400">
              Du vil få svar på e-post så snart vi har behandlet saken din.
              Bruk saksnummeret ved eventuelle oppfølgings-spørsmål.
            </p>
          </div>
          <button
            onClick={() => setResult(null)}
            className="btn-primary w-full"
          >
            Send ny sak
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950">
      {/* Header – gradient + glassmorphism */}
      <header className="sticky top-0 z-10 border-b border-slate-200/60 bg-white/80 backdrop-blur-xl dark:border-slate-800/60 dark:bg-slate-900/80">
        <div className="mx-auto flex max-w-2xl items-center gap-3 px-4 py-4">
          <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-brand shadow-glow">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" className="h-6 w-6 text-white">
              <path d="M3 7v10a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V7" />
              <path d="M3 7l2-4h14l2 4" />
              <path d="M3 7h18" />
            </svg>
          </div>
          <div>
            <h1 className="text-base font-semibold text-slate-800 dark:text-slate-100">Kundeservice</h1>
            <p className="text-xs text-slate-500 dark:text-slate-400">Send inn henvendelse</p>
          </div>
        </div>
      </header>

      {/* Skjema */}
      <main className="mx-auto max-w-2xl px-4 py-8">
        {/* Hero-seksjon */}
        <div className="mb-6 animate-fade-in-up text-center">
          <h2 className="mb-2 text-2xl font-bold text-slate-800 dark:text-slate-100">
            Hvordan kan vi hjelpe?
          </h2>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            Fyll ut skjemaet under, så kommer vi tilbake til deg på e-post
          </p>
        </div>

        <div className="card animate-fade-in-up p-6 md:p-8" style={{ animationDelay: '0.1s' }}>
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Kategori – visuelle kort (påkrevd, øverst) */}
            <div>
              <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-200">
                Hva gjelder det? <span className="text-red-500">*</span>
              </label>
              {categoriesLoading ? (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {[1, 2, 3].map((i) => (
                    <div key={i} className="h-20 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800" />
                  ))}
                </div>
              ) : categories.length === 0 ? (
                <p className="rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-700 dark:bg-amber-500/10 dark:text-amber-400">
                  Ingen kategorier tilgjengelig. Kontakt support direkte.
                </p>
              ) : (
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
                  {categories.map((cat) => {
                    const selected = kategori === cat.navn;
                    return (
                      <button
                        key={cat.id}
                        type="button"
                        onClick={() => !submitting && setKategori(cat.navn)}
                        disabled={submitting}
                        className={clsx(
                          'group relative flex flex-col items-start gap-1 rounded-2xl border-2 p-4 text-left transition-all duration-200',
                          selected
                            ? 'border-transparent shadow-lg'
                            : 'border-slate-200 hover:border-slate-300 hover:shadow-md dark:border-slate-700 dark:hover:border-slate-600',
                          submitting && 'cursor-not-allowed opacity-50',
                        )}
                        style={selected ? { backgroundColor: cat.farge, borderColor: cat.farge } : undefined}
                        aria-pressed={selected}
                      >
                        <span
                          className="h-2.5 w-2.5 rounded-full transition-transform group-hover:scale-110"
                          style={{ backgroundColor: selected ? '#fff' : cat.farge }}
                        />
                        <span
                          className={clsx(
                            'text-sm font-semibold leading-tight',
                            selected ? 'text-white' : 'text-slate-700 dark:text-slate-200',
                          )}
                        >
                          {cat.navn}
                        </span>
                        {cat.beskrivelse && (
                          <span
                            className={clsx(
                              'text-xs leading-tight line-clamp-2',
                              selected ? 'text-white/80' : 'text-slate-400 dark:text-slate-500',
                            )}
                          >
                            {cat.beskrivelse}
                          </span>
                        )}
                        {selected && (
                          <svg className="absolute right-2 top-2 h-4 w-4 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
                            <path d="M20 6 9 17l-5-5" />
                          </svg>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>

            {/* Navn + E-post i grid på desktop */}
            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="navn" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
                  Navn *
                </label>
                <input
                  id="navn"
                  type="text"
                  required
                  value={navn}
                  onChange={(e) => setNavn(e.target.value)}
                  placeholder="Ditt fulle navn"
                  className="input"
                  autoComplete="name"
                  disabled={submitting}
                />
              </div>

              <div>
                <label htmlFor="epost" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
                  E-post *
                </label>
                <input
                  id="epost"
                  type="email"
                  required
                  value={epost}
                  onChange={(e) => setEpost(e.target.value)}
                  placeholder="din@epost.no"
                  className="input"
                  autoComplete="email"
                  disabled={submitting}
                />
              </div>
            </div>

            {/* Tittel */}
            <div>
              <label htmlFor="tittel" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
                Emne *
              </label>
              <input
                id="tittel"
                type="text"
                required
                value={tittel}
                onChange={(e) => setTittel(e.target.value)}
                placeholder="Kort beskrivelse av problemet"
                className="input"
                disabled={submitting}
              />
            </div>

            {/* Beskrivelse */}
            <div>
              <label htmlFor="beskrivelse" className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
                Beskrivelse
              </label>
              <textarea
                id="beskrivelse"
                value={beskrivelse}
                onChange={(e) => setBeskrivelse(e.target.value)}
                placeholder="Beskriv problemet ditt så detaljert som mulig…"
                rows={6}
                className="input resize-y"
                disabled={submitting}
              />
              <p className="mt-1 text-xs text-slate-400">
                {beskrivelse.length} tegn
              </p>
            </div>

            {/* Vedlegg – drag & drop style */}
            <div>
              <label className="mb-1.5 block text-sm font-medium text-slate-700 dark:text-slate-200">
                Vedlegg (valgfritt)
              </label>
              <div
                onClick={() => !submitting && fileInputRef.current?.click()}
                className={clsx(
                  'cursor-pointer rounded-2xl border-2 border-dashed p-6 text-center transition-all duration-200',
                  file
                    ? 'border-brand-400 bg-brand-50/50 dark:border-brand-500/50 dark:bg-brand-500/5'
                    : 'border-slate-300 hover:border-brand-400 hover:bg-brand-50/50 dark:border-slate-700 dark:hover:border-brand-500/50 dark:hover:bg-brand-500/5',
                  submitting && 'cursor-not-allowed opacity-50',
                )}
              >
                {file ? (
                  <>
                    <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-brand-100 dark:bg-brand-500/20">
                      <svg className="h-5 w-5 text-brand-600 dark:text-brand-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21.44 11.05l-9.19 9.19a6 6 0 0 1-8.49-8.49l9.19-9.19a4 4 0 0 1 5.66 5.66l-9.2 9.19a2 2 0 0 1-2.83-2.83l8.49-8.48" />
                      </svg>
                    </div>
                    <p className="text-sm font-medium text-slate-700 dark:text-slate-200">{file.name}</p>
                    <p className="text-xs text-slate-400">({(file.size / 1024).toFixed(0)} KB) – klikk for å bytte</p>
                  </>
                ) : (
                  <>
                    <div className="mx-auto mb-2 flex h-10 w-10 items-center justify-center rounded-xl bg-slate-100 dark:bg-slate-800">
                      <svg className="h-5 w-5 text-slate-400" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                        <polyline points="17 8 12 3 7 8" />
                        <line x1="12" y1="3" x2="12" y2="15" />
                      </svg>
                    </div>
                    <p className="text-sm text-slate-500 dark:text-slate-400">Klikk for å velge en fil</p>
                    <p className="text-xs text-slate-400">Maks 10 MB</p>
                  </>
                )}
                <input
                  ref={fileInputRef}
                  type="file"
                  className="hidden"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  disabled={submitting}
                />
              </div>
              {file && (
                <button
                  type="button"
                  onClick={() => setFile(null)}
                  className="mt-1.5 text-xs text-slate-500 hover:text-red-600 dark:text-slate-400"
                >
                  Fjern fil
                </button>
              )}
            </div>

            {/* Honeypott – skjult for ekte brukere */}
            <input
              type="text"
              name="website"
              tabIndex={-1}
              autoComplete="off"
              style={{ position: 'absolute', left: '-9999px', width: '1px', height: '1px', opacity: 0 }}
              aria-hidden="true"
            />

            {/* Feilmelding */}
            {error && (
              <div className="animate-fade-in rounded-xl bg-red-50 px-4 py-3 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-400">
                {error}
              </div>
            )}

            {/* Send-knapp */}
            <button
              type="submit"
              className="btn-primary w-full text-base"
              disabled={submitting || !navn.trim() || !epost.trim() || !tittel.trim() || !kategori}
            >
              {submitting ? (
                <>
                  <svg className="h-5 w-5 animate-spin" viewBox="0 0 24 24" fill="none">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 0 1 8-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Sender…
                </>
              ) : (
                'Send henvendelse'
              )}
            </button>
          </form>
        </div>

        {/* Tillitselementer */}
        <div className="mt-6 flex items-center justify-center gap-6 text-xs text-slate-400">
          <div className="flex items-center gap-1.5">
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
            </svg>
            Sikker
          </div>
          <div className="flex items-center gap-1.5">
            <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <circle cx="12" cy="12" r="10" />
              <polyline points="12 6 12 12 16 14" />
            </svg>
            Svar innen 1-2 virkedager
          </div>
        </div>
      </main>
    </div>
  );
}
